// =========================================================
// Vojjin Scroll Utilities
// =========================================================
import {registerGlobal} from "./core.js";

/* global Logger */
class Scroll {

    /**
     * Scrolls the window to the top of the page.
     *
     * @param {('auto'|'smooth')} [behavior='smooth'] - Defines the scrolling animation behavior.
     *                                       Can be 'auto' for instant scrolling or 'smooth' for animated scrolling.
     */
    static top = (behavior = 'smooth') => {
        window.scrollTo({top: 0, behavior});
    }

    /**
     * Scrolls the window to the bottom of the page.
     *
     * @param {('auto'|'smooth')} [behavior='smooth'] - Defines the scrolling animation behavior.
     *        Accepts 'smooth' for animated scrolling or 'auto' for immediate scrolling.
     */
    static bottom = (behavior = 'smooth') => {
        window.scrollTo({top: document.body.scrollHeight, behavior});
    }

    /**
     * Scrolls the page to the given element with specified options.
     * If the element is not found, a warning is logged.
     *
     * @param {Element|string|DomWrapper} el - The target element or a selector string to scroll to.
     * @param {Object} [opts={}] - Optional configuration object.
     * @param {string} [opts.behavior='smooth'] - Specifies the scroll behavior, e.g., 'smooth' or 'auto'.
     * @param {number} [opts.offset=0] - A vertical offset to adjust the final scroll position.
     */
    static to = (el, opts = {}) => {
        el = $$(el);
        if (!el.defined()) {
            Logger.warn("Scroll.to: element not found");
            return;
        }
        const behavior = opts.behavior ?? 'smooth';
        const offset = opts.offset ?? 0;
        const rect = el.element.getBoundingClientRect();
        const scrollTop = window.scrollY + rect.top - offset;
        window.scrollTo({top: scrollTop, behavior});
    }

    /**
     * Scrolls the content of a container element to bring a target element into view.
     *
     * @param {Element|string|DomWrapper} container - The container element or a CSS selector string of the container to scroll.
     * @param {Element|string|DomWrapper} el - The target element or a CSS selector string of the element to bring into view.
     * @param {Object} [opts={}] - Optional configuration for the scrolling behavior.
     * @param {number} [opts.offset=0] - The pixel offset to apply to the scroll position.
     * @param {string} [opts.behavior='smooth'] - The scroll behavior, e.g., 'smooth' or 'auto'.
     */
    static in = (container, el, opts = {}) => {
        container = $$(container);
        el = $$(el);
        if (!container.defined() || !el.defined()) return;
        const rect = el.element.getBoundingClientRect();
        const cRect = container.element.getBoundingClientRect();
        const offset = opts.offset ?? 0;
        container.element.scrollTo({
            top: container.element.scrollTop + (rect.top - cRect.top - offset),
            behavior: opts.behavior ?? 'smooth'
        });
    }

    /**
     * Determines whether a given element is visible within the viewport,
     * optionally considering a threshold for partial visibility.
     *
     * @param {Element|string|DomWrapper} el - The DOM element or selector to evaluate for visibility.
     * @param {number} [threshold=0] - The margin in pixels from the viewport edges
     *     that affects visibility calculation. Defaults to 0.
     * @returns {boolean} - Returns `true` if the element is visible within the
     *     viewport and meets the given threshold, otherwise `false`.
     */
    static isVisible = (el, threshold = 0) => {
        el = $$(el);
        if (!el.defined()) return false;
        const rect = el.element.getBoundingClientRect();
        return (
            rect.top < window.innerHeight - threshold &&
            rect.bottom > threshold &&
            rect.left < window.innerWidth &&
            rect.right > 0
        );
    }

    /**
     * Prevents scrolling on the page by setting the body's overflow style to 'hidden'.
     * This function can be used to lock the viewport, typically during modals, popups, or other UI states
     * where scrolling should be disabled.
     *
     * @returns {Object} The current context (`this`) to allow method chaining.
     */
    static lock = () => {
        document.body.style.overflow = 'hidden';
        return this;
    }

    /**
     * Re-enables scrolling on the webpage by resetting the `overflow` style property of the document body.
     *
     * @function unlock
     * @returns {Object} The current context (`this`) to allow for method chaining.
     */
    static unlock = () => {
        document.body.style.overflow = '';
        return this;
    }

    /**
     * Calculates the progress of the page scroll as a fraction between 0 and 1.
     *
     * The progress is determined by dividing the current vertical scroll position by the total
     * scrollable height of the document. If the document has no scrollable height, the progress
     * will be 0.
     *
     * @returns {number} A number between 0 and 1 representing the scroll progress,
     *                   where 0 indicates no scroll and 1 indicates the bottom of the page.
     */
    static progress = () => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        return docHeight > 0 ? scrollTop / docHeight : 0;
    }
}

registerGlobal('Scroll', Scroll);
